import React, { useEffect, useRef, useState } from "react";
import {
  BrainCircuit,
  Calendar,
  Camera,
  CarFront,
  Laptop,
  Palette,
  Route,
  UserRound,
  Users,
  GraduationCap,
  Video,
} from "lucide-react";
import { kidpointingdown, zoomcall } from "../../assets/images";
import styles from "../../pages/styles";

const FreeTrialAbout = () => {
  const [isVisible, setIsVisible] = useState(false);
  const sectionRef = useRef(null);
  const hasAnimatedRef = useRef(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && !hasAnimatedRef.current) {
            setIsVisible(true);
            hasAnimatedRef.current = true;
            // Unobserve after animation triggers to prevent re-triggering
            observer.unobserve(entry.target);
          }
        });
      },
      { 
        threshold: 0.7,
        rootMargin: '0px 0px -100px 0px'
      }
    );

    const currentRef = sectionRef.current;
    if (currentRef) {
      observer.observe(currentRef);
    }

    return () => {
      if (currentRef) {
        observer.unobserve(currentRef);
      }
    };
  }, []);

  const benefits = [
    { title: "Flexible", img: <Calendar className="w-10 h-10 text-white" /> },
    { title: "Expert Teachers", img: <GraduationCap className="w-10 h-10 text-white" /> },
    { title: "No Driving", img: <CarFront className="w-10 h-10 text-white" /> },
    { title: "Easy Setup", img: <Laptop className="w-10 h-10 text-white" /> },
    { title: "Live on Zoom", img: <Video className="w-10 h-10 text-white" /> },
  ];

  return (
    <div className="bg-darkprimary flex flex-col items-center justify-center w-full pt-8 pb-28 md:pt-10 md:pb-32 relative" ref={sectionRef}>
      <div className="text-center max-w-4xl px-2 md:px-4 mb-6 md:mb-8">
        <h2 className={`${styles.biggerheadingwhite} mb-3 md:mb-4 relative`}>
          <span className="relative inline-block z-10">
            100%
            <span className="absolute shadow-sm bottom-[-2px] left-1 w-full h-2 bg-pinkprimary z-0 -skew-x-[30deg] rounded-sm"></span>
          </span> Virtual — Learn From Anywhere
        </h2>
        <p className={`${styles.paragraphwhite}`}>
          Expert live instruction delivered right to your home. No driving, no hassle—just your child 
          learning valuable tech skills in a comfortable, focused environment.
        </p>
      </div>
      <div className="flex flex-wrap justify-evenly items-start w-full md:px-24 gap-6 md:gap-8">
        {benefits.map((benefit, index) => (
          <BenefitCard
            key={index}
            title={benefit.title}
            img={benefit.img}
            index={index}
            isVisible={isVisible}
          />
        ))}
      </div>

      <div className="absolute -bottom-[52px] sm:-bottom-[74px] -left-4 sm:left-10 w-[280px] sm:w-[400px]">
        <img src={kidpointingdown} alt="Kid pointing down" />
      </div>
    </div>
  );
};

const BenefitCard = ({ title, desc, img, index, isVisible }) => {
  return (
    <div 
      className={`benefit-card-entrance flex flex-col items-center text-center w-[120px] mx-2 md:mx-4 ${
        isVisible ? 'benefit-card-visible' : ''
      }`}
      style={{
        "--fade-delay": `${index * 0.15}s`,
      }}
    >
      <div className="mb-3">
        {img}
      </div>
      <p className={`${styles.paragraphwhite} mb-0 whitespace-nowrap`}>
        {title}
      </p>
    </div>
  );
};

export default FreeTrialAbout;
